<template>
    <FilterComponent :displayText="facet.title" ref="filter" class="checkbox-facet">
        <div v-for="group in options" :key="group.value" :data-depth="group.depth">
            <label :class="{ noselect: true, grouping: group.grouping }" :style="{ 'padding-left': group.indent }">
                <input
                    type="checkbox"
                    @input="value => toggleGroup(group, value)"
                    :checked="group.checked"
                    :indeterminate.prop="group.indeterminate"
                    :class="{ indeterminate: group.indeterminate }"
                    v-if="!group.grouping"
                />

                <span>{{ group.display }}</span>
            </label>
        </div>
    </FilterComponent>
</template>
<script setup>
import FilterComponent from '../FilterComponent.vue'
import { computed } from 'vue'

const props = defineProps({
    facet: {
        type: Object,
        required: true,
    },
    criteria: {
        type: Array,
        default() {
            return []
        },
    },
})

const emit = defineEmits(['update:criteria'])

const options = computed(() => {
    const flat = []

    const visit = (v, depth) => {
        const current = {
            ...v,
            depth,
            indent: `${depth * 14}px`,
        }

        flat.push(current)

        current.children = v.children?.map(c => visit(c, depth + 1)) ?? []

        if (current.children.length === 0) {
            current.checked = selected.value.has(current.value)
            current.indeterminate = false
        } else {
            current.checked = current.children.reduce((a, c) => a && c.checked, true)
            current.indeterminate = !current.checked && current.children.some(c => c.checked || c.indeterminate)
        }

        return current
    }

    visit(
        {
            display: props.facet.selectAllText,
            children: props.facet.values,
            value: '__root__',
        },
        -1
    )

    flat[0].depth = 0
    flat[0].indent = '0'

    return flat
})

const selected = computed(() => {
    const name = props.facet.name
    return new Set(props.criteria.filter(c => c.name === name).map(c => c.value))
})

function toggleGroup(group, event) {
    if (event.target.checked) {
        addGroup(group)
    } else {
        removeGroup(group)
    }
}

function addGroup(group) {
    const criteria = props.criteria.slice()
    const name = props.facet.name

    const existing = new Set(criteria.filter(c => c.name === name).map(c => c.value))

    const queue = [group]

    while (queue.length > 0) {
        group = queue.shift()

        if (group.children.length > 0) {
            queue.push(...group.children)
            continue
        }

        if (!existing.has(group.value)) {
            criteria.push({
                display: group.display,
                name,
                value: group.value,
            })
        }
    }

    emit('update:criteria', criteria)
}

function removeGroup(group) {
    const criteria = props.criteria.slice()
    const name = props.facet.name

    const existing = new Set(criteria.filter(c => c.name === name).map(c => c.value))

    const queue = [group]

    while (queue.length > 0) {
        group = queue.shift()

        if (group.children.length > 0) {
            queue.push(...group.children)
            continue
        }

        if (existing.has(group.value)) {
            criteria.splice(
                criteria.findIndex(c => c.name === name && c.value === group.value),
                1
            )
        }
    }

    emit('update:criteria', criteria)
}
</script>

<style>
.checkbox-facet {
    label {
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: 8px;
        padding: 4px;
        white-space: nowrap;
    }

    label.grouping {
        font-weight: bold;
    }

    input[type='checkbox']::before {
        float: none;
        left: 0;
        margin: 0;
        padding: 0;
    }

    [data-depth='0']:first-child {
        padding-bottom: 8px;
        border-bottom: 2px solid var(--color-primary-200);
    }

    [data-depth]:not([data-depth='0']) + [data-depth='0'] {
        border-top: 1px solid var(--color-primary-200);
        margin-top: 4px;
    }
}
</style>
